import { useCallback, useState } from "react";
import { useLocation } from "react-router";
import { useMaquinasEquipItensControlePagination } from "../hooks/MaquinasEquipItens_controle/pagination";
import { useMaquinasEquipItensControleFilters } from "../hooks/MaquinasEquipItens_controle/filter";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { InspecaoMaquinaEquipLista } from "../../domain/models/inspecao_maquina_equip_lista";
import { MaquinaEquipDropdown } from "../../domain/models/maquina_equip_dropdown";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { ControleEstatisticas } from "../../domain/models/controle_estatisticas";
import { ControleCentroCustoDetalhe } from "../../domain/models/controle_centro_custo_detalhe";
import { ControleInspecaoMaquinaForm } from "../schemas/MaquinasEquipItens_controle/MaquinasEquipItens_controle";
import { CopyInspecaoMaquinaEquipForm } from "../schemas/MaquinasEquipItens_controle/copy_inspecao_maquina_equip";


type LoadMaquinaEquipInspecoesFuncState = {
  idControleCentroCusto?: number;
  maquinaEquip?: number[] | null;
  data?: Date | null;
  page?: number;
  rowsPerPage?: number;
  dataEntradaDe?: Date | null,
  dataEntradaAte?: Date | null,
  codigo?: string | null
};


export const useMaquinasEquipItensControleViewModel = () => {
  const {
    controlePatrimonio: {
      dropDownMaquinaEquipUseCase,
      getAllControledePatrimonioInspecaoUseCase,
      getControleCentroCustoByIdUseCase,
      getControleCentroCustoEstatisticasUseCase,
      updateMetaStatusControleCentroCustoUseCase,
      createControledePatrimonioInspecaoUseCase,
      updateControledePatrimonioInspecaoUseCase,
      deleteControledePatrimonioInspecaoUseCase,
      copyInspecaoMaquinaEquipUseCase
    },
  } = useDependencyInjection();

  const { pushErrorNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const location = useLocation();

  const debouncedHandler = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idControleCentroCusto, setIdControleCentroCusto] = useState<number>();

  const [controleCentroCusto, setControleCentroCusto] = useState<ControleCentroCustoDetalhe>();
  const [inspecoesMaquinaEquip, setInspecoesMaquinaEquip] = useState<InspecaoMaquinaEquipLista[]>();
  const [estatisticasControle, setEstatisticasControle] = useState<ControleEstatisticas>();

  const [selectedInspecaoMaquinaEquip, setSelectedInspecaoMaquinaEquip] = useState<InspecaoMaquinaEquipLista>();

  const [maquinaEquipDropDown, setMaquinaEquipDropDown] = useState<MaquinaEquipDropdown[]>();

  const [statusControle, setStatusControle] = useState<number | null>(null);
  const [metaControle, setMetaControle] = useState<number | null>(null);

  const [inspecaoMaquinaEquipModal, setInspecaoMaquinaEquipModal] = useState(false);
  const [inspecaoMaquinaEquipDeleteModal, setInspecaoMaquinaEquipDeleteModal] = useState(false);
  const [inspecaoMaquinaEquipGraficoModal, setInspecaoMaquinaEquipGraficoModal] = useState(false);
  const [copyInspecaoMaquinaEquipModal, setCopyInspecaoMaquinaEquipModal] = useState(false);

  const filters = useMaquinasEquipItensControleFilters();
  const pagination = useMaquinasEquipItensControlePagination();

  /**
   * Funcoes de carregamento
   */

  const loadMaquinaEquipDropDown = useCallback(async () => {
    try {
      let results = await dropDownMaquinaEquipUseCase.dropDownMaquinaEquip();
      setMaquinaEquipDropDown(results);
    } catch (e: unknown) {
      console.error("Erro ao carregar dropDown:", e);
    }
  }, [
    dropDownMaquinaEquipUseCase,
  ]);

  const loadControleCentroCusto = useCallback(async (id?: number) => {
    try {
      const filteredIdControleCentroCusto = id ?? idControleCentroCusto;
      const results = await getControleCentroCustoByIdUseCase.getControleCentroCustoById(filteredIdControleCentroCusto);
      setStatusControle(results.status);
      setMetaControle(results.metaAprovacao);
      setControleCentroCusto(results);
    } catch (e: unknown) {
      setControleCentroCusto(undefined);
      throw new Error('Falha ao buscar dados do controle de centro de custo!');
    }
  }, [
    idControleCentroCusto,
    getControleCentroCustoByIdUseCase,
  ]);

  const loadControleEstatisticas = useCallback(async (id?: number) => {
    try {
      const filteredIdControleCentroCusto = id ?? idControleCentroCusto;
      const results = await getControleCentroCustoEstatisticasUseCase.getControleCentroCustoEstatisticas(filteredIdControleCentroCusto);
      setEstatisticasControle(results);
    } catch (e: unknown) {
      setEstatisticasControle(undefined);
      throw new Error('Falha ao buscar dados do controle de centro de custo!');
    }
  }, [
    idControleCentroCusto,
    getControleCentroCustoEstatisticasUseCase
  ]);

  const loadMaquinaEquipInspecoes = useCallback(async (fs?: LoadMaquinaEquipInspecoesFuncState) => {
    try {
      const filterIdControleCentroCusto = fs?.idControleCentroCusto ?? idControleCentroCusto;
      const filterMaquinaEquip = evaluateParamValue(fs?.maquinaEquip, filters.maquinaEquip);
      const filteredDataEntradaAte = fs?.dataEntradaAte ;
      const filteredDataEntradaDe = fs?.dataEntradaDe;
      const filteredCodigo = fs?.codigo ?? filters.codigo;

      const filterPage = fs?.page ?? pagination.page;
      const filterRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllControledePatrimonioInspecaoUseCase.getAllControledePatrimonioInspecao(
        filterIdControleCentroCusto,
        filterMaquinaEquip,
        filterPage,
        filterRowsPerPage,
        filteredDataEntradaAte,
        filteredDataEntradaDe,
        filteredCodigo
      );

      setInspecoesMaquinaEquip(getAllControledePatrimonioInspecaoUseCase.items);
      pagination.setTotalItems(getAllControledePatrimonioInspecaoUseCase.totalItems);
    } catch (e: unknown) {
      setInspecoesMaquinaEquip(undefined);
      throw new Error('Falha ao buscar dados de inspeção de máquinas e equipamentos!');
    }
  }, [idControleCentroCusto, filters.maquinaEquip, filters.codigo, pagination, getAllControledePatrimonioInspecaoUseCase]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");
      const id = locationId ?? undefined;

      if (!id || id.length === 0) {
        setErrorMessage("Falha ao recuperar id do checklist!");
        return;
      }
      setIdControleCentroCusto(Number(id));

      await loadMaquinaEquipDropDown();
      await loadControleCentroCusto(Number(id));
      await loadControleEstatisticas(Number(id));
      await loadMaquinaEquipInspecoes({ idControleCentroCusto: Number(id) });

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das páginas!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadMaquinaEquipDropDown();
      await loadControleCentroCusto();
      await loadControleEstatisticas();
      await loadMaquinaEquipInspecoes();

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao recarregar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadMaquinaEquipDropDown,
    loadControleCentroCusto,
    loadControleEstatisticas,
    loadMaquinaEquipInspecoes,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: "/MaquinasEquip_controle" });
  }, [
    backOrNavigate,
  ]);

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useMaquinasEquipItensControlePagination.setState({ page: page + 1 });
        await loadMaquinaEquipInspecoes({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, loadMaquinaEquipInspecoes],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useMaquinasEquipItensControlePagination.setState({
          page: 1,
          rowsPerPage,
        });
        await loadMaquinaEquipInspecoes({ page: 1, rowsPerPage: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, loadMaquinaEquipInspecoes],
  );

  const handleInspecaoMaquinaEquipOpenState = useCallback((idInspecaoMaquinaEquip: number | null, openState: boolean) => {
    if (openState) {
      if (idInspecaoMaquinaEquip && inspecoesMaquinaEquip) {
        setSelectedInspecaoMaquinaEquip(inspecoesMaquinaEquip.find(i => i.idInspecaoMaquinaEquip === idInspecaoMaquinaEquip));
      } else {
        setSelectedInspecaoMaquinaEquip(undefined);
      }
    }
    setInspecaoMaquinaEquipModal(openState);
  }, [
    inspecoesMaquinaEquip,
  ]);

  const handleInspecaoMaquinaEquipSubmit = useCallback(async (form: ControleInspecaoMaquinaForm) => {
    if (idControleCentroCusto) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedInspecaoMaquinaEquip) {
          await updateControledePatrimonioInspecaoUseCase.updateControledePatrimonioInspecao(
            idControleCentroCusto,
            selectedInspecaoMaquinaEquip.idInspecaoMaquinaEquip,
            form.dataInspecao,
            form.idMaquinaEquip,
            form.codigoMaquinaEquip,
          );
        } else {
          await createControledePatrimonioInspecaoUseCase.createControledePatrimonioInspecao(
            idControleCentroCusto,
            form.dataInspecao,
            form.idMaquinaEquip,
            form.codigoMaquinaEquip,
          );
        }

        await loadMaquinaEquipInspecoes();

        setSelectedInspecaoMaquinaEquip(undefined);
        setInspecaoMaquinaEquipModal(false);

        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao atualizar status do controle de centro de custo!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idControleCentroCusto,
    selectedInspecaoMaquinaEquip,
    createControledePatrimonioInspecaoUseCase,
    updateControledePatrimonioInspecaoUseCase,
    loadMaquinaEquipInspecoes,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleInspecaoMaquinaEquipDeleteOpenState = useCallback((idInspecaoMaquinaEquip: number | null, openState: boolean) => {
    if (openState) {
      if (idInspecaoMaquinaEquip && inspecoesMaquinaEquip) {
        setSelectedInspecaoMaquinaEquip(inspecoesMaquinaEquip.find(i => i.idInspecaoMaquinaEquip === idInspecaoMaquinaEquip));
      } else {
        setSelectedInspecaoMaquinaEquip(undefined);
      }
    }
    setInspecaoMaquinaEquipDeleteModal(openState);
  }, [
    inspecoesMaquinaEquip,
  ]);

  const handleInspecaoMaquinaEquipDeleteSubmit = useCallback(async () => {
    if (idControleCentroCusto && selectedInspecaoMaquinaEquip) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteControledePatrimonioInspecaoUseCase.deleteControledePatrimonioInspecao(
          idControleCentroCusto,
          selectedInspecaoMaquinaEquip.idInspecaoMaquinaEquip,
        );

        await loadMaquinaEquipInspecoes();

        setSelectedInspecaoMaquinaEquip(undefined);
        setInspecaoMaquinaEquipDeleteModal(false);

        pushInfoNotification("Sucesso", "Registro excluído com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao excluir registro!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idControleCentroCusto,
    selectedInspecaoMaquinaEquip,
    deleteControledePatrimonioInspecaoUseCase,
    loadMaquinaEquipInspecoes,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleCopyInspecaoMaquinaEquipModalOpenState = useCallback((idInspecaoMaquinaEquip: number | null, openState: boolean) => {
    if (openState) {
      if (idInspecaoMaquinaEquip && inspecoesMaquinaEquip) {
        setSelectedInspecaoMaquinaEquip(inspecoesMaquinaEquip.find(i => i.idInspecaoMaquinaEquip === idInspecaoMaquinaEquip));
      } else {
        setSelectedInspecaoMaquinaEquip(undefined);
      }
    }
    setCopyInspecaoMaquinaEquipModal(openState);
  }, [
    inspecoesMaquinaEquip
  ]);

  const handleCopyInspecaoMaquinaEquipSubmit = useCallback(async (form: CopyInspecaoMaquinaEquipForm) => {
    if (idControleCentroCusto && selectedInspecaoMaquinaEquip) {
      try {
        setSaving(true);
        setPageSaving(true);

        await copyInspecaoMaquinaEquipUseCase.copyInspecaoMaquinaEquip(
          idControleCentroCusto,
          selectedInspecaoMaquinaEquip.idInspecaoMaquinaEquip,
          form.nroCopias,
        );

        await loadMaquinaEquipInspecoes();

        setSelectedInspecaoMaquinaEquip(undefined);
        setCopyInspecaoMaquinaEquipModal(false);

        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao copiar registro!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idControleCentroCusto,
    selectedInspecaoMaquinaEquip,
    copyInspecaoMaquinaEquipUseCase,
    loadMaquinaEquipInspecoes,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleInspecaoMaquinaEquipGraficoModalOpenState = useCallback((openState: boolean) => {
    setInspecaoMaquinaEquipGraficoModal(openState);
  }, [
  ]);

  const handleInspecaoMaquinaEquipEnter = useCallback((idInspecaoMaquinaEquip: number, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/MaquinasEquipItens_verificacao?idControleCentroCusto=${idControleCentroCusto}&id=${idInspecaoMaquinaEquip}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/MaquinasEquipItens_verificacao?idControleCentroCusto=${idControleCentroCusto}&id=${idInspecaoMaquinaEquip}`);
    }
  }, [
    backOrNavigate,
    idControleCentroCusto,
  ]);

  const handleControleCentroCustoStatusChange = useCallback(async (status: number) => {
    if (idControleCentroCusto) {
      try {
        setSaving(true);
        setPageSaving(true);

        setStatusControle(status);

        await updateMetaStatusControleCentroCustoUseCase.updateMetaStatusControleCentroCusto(
          idControleCentroCusto,
          metaControle,
          status,
        );

        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao atualizar status do controle de centro de custo!"));
      } finally {
        try {
          await loadControleCentroCusto();
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao atualizar status do controle de centro de custo!"));
        }
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idControleCentroCusto,
    metaControle,
    updateMetaStatusControleCentroCustoUseCase,
    loadControleCentroCusto,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleControleCentroCustoMetaChange = useCallback(async (meta: number | null) => {
    setMetaControle(meta);
  }, [
  ]);

  const handleControleCentroCustoBlur = useCallback(async () => {
    if (idControleCentroCusto) {
      try {
        setSaving(true);
        setPageSaving(true);

        await updateMetaStatusControleCentroCustoUseCase.updateMetaStatusControleCentroCusto(
          idControleCentroCusto,
          metaControle,
          statusControle,
        );

        await loadControleCentroCusto();
        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao atualizar status do controle de centro de custo!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [idControleCentroCusto, setPageSaving, updateMetaStatusControleCentroCustoUseCase, metaControle, statusControle, loadControleCentroCusto, pushInfoNotification, pushErrorNotification]);

  const handleMaquinaEquipChange = useCallback(async (maquinaEquip: number[] | null) => {
    try {
      setLoading(true);
      useMaquinasEquipItensControleFilters.setState({ maquinaEquip });
      useMaquinasEquipItensControlePagination.setState({ page: 1 });
      await loadMaquinaEquipInspecoes({ maquinaEquip, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [loadMaquinaEquipInspecoes, pushErrorNotification]);

   const handleDataReceberRangeChange = useDebouncedCallback(debouncedHandler,
    (dataEntradaDe: Date | null, dataEntradaAte: Date | null) => {
      useMaquinasEquipItensControleFilters.setState({ dataEntradaDe });
      useMaquinasEquipItensControleFilters.setState({ dataEntradaAte });

      return async () => {
        try {
          setLoading(true);
          await loadMaquinaEquipInspecoes({ dataEntradaDe, dataEntradaAte, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar produtos!"));
        } finally {
          setLoading(false);
        }
      };
    },
  [ filters]
  );
  const handleCodigoChange = useDebouncedCallback(debouncedHandler, (newValue: string) => {
    useMaquinasEquipItensControleFilters.setState({ codigo: newValue });
    return async () => {
      try {
        setLoading(true);
        await loadMaquinaEquipInspecoes({ codigo: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadMaquinaEquipInspecoes, pushErrorNotification]);
  


  const handleClear = useCallback(async () => {
    try {
      setLoading(true);
      useMaquinasEquipItensControleFilters.getState().clear();
      await loadMaquinaEquipInspecoes({ maquinaEquip: null, data: null, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao atualizar página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadMaquinaEquipInspecoes,
    pushErrorNotification,
  ]);

  const handleEnterInspecaoObra = useCallback(
    (idInspecaoObra: number, openNewTab: boolean) => {
      if (openNewTab) {
        window.open(`/MaquinasEquipItens_verificacao?id=${idInspecaoObra}`, "_blank", "noopener,noreferrer");
      } else {
        backOrNavigate(`/MaquinasEquipItens_verificacao?id=${idInspecaoObra}`, {
          alternativeRoute: `/MaquinasEquipItens_verificacao?id=${idInspecaoObra}`,
        });
      }
    },
    [backOrNavigate],
  );

  return {
    loading,
    saving,
    errorMessage,
    inspecoesMaquinaEquip: inspecoesMaquinaEquip ?? [],
    controleCentroCusto: controleCentroCusto ?? null,
    estatisticasControle: estatisticasControle ?? null,
    selectedInspecaoMaquinaEquip: selectedInspecaoMaquinaEquip ?? null,
    maquinaEquipDropDown: maquinaEquipDropDown ?? [],
    statusControle,
    metaControle,
    inspecaoMaquinaEquipModal,
    inspecaoMaquinaEquipDeleteModal,
    inspecaoMaquinaEquipGraficoModal,
    copyInspecaoMaquinaEquipModal,
    filters,
    pagination,
    handleReload,
    handleBack,
    handlePageChange,
    handleRowsPerPageChange,
    handleInspecaoMaquinaEquipOpenState,
    handleInspecaoMaquinaEquipSubmit,
    handleInspecaoMaquinaEquipDeleteOpenState,
    handleInspecaoMaquinaEquipDeleteSubmit,
    handleCopyInspecaoMaquinaEquipModalOpenState,
    handleCopyInspecaoMaquinaEquipSubmit,
    handleInspecaoMaquinaEquipGraficoModalOpenState,
    handleInspecaoMaquinaEquipEnter,
    handleControleCentroCustoStatusChange,
    handleControleCentroCustoMetaChange,
    handleControleCentroCustoBlur,
    handleMaquinaEquipChange,
    handleClear,
    handleEnterInspecaoObra,
    handleDataReceberRangeChange,
    handleCodigoChange
  };
};
